import { createContext, useContext, ReactNode, useState, useEffect } from "react";
import { ENTITY_CONFIGS, EntityTypeKey } from "@/other/constants/EntityManagerConfig";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys";
import { EntityRecord } from "@/types/entity";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { EntityManagerOtherParams } from "@/react/schema/entityManagerSchema";

interface EntityManagerContextValue {
  record: EntityRecord;
  entityType: EntityTypeKey;
  entityConfig: (typeof ENTITY_CONFIGS)[keyof typeof ENTITY_CONFIGS];
  activeTab: string;
  setActiveTab: (tab: string) => void;
  otherParams: EntityManagerOtherParams | null;
  closeEntityManager: () => void;
}

export const EntityManagerContext = createContext<EntityManagerContextValue | undefined>(undefined);

interface EntityManagerProviderProps {
  children: ReactNode;
}

export const EntityManagerProvider = ({ children }: EntityManagerProviderProps) => {
  const { entityManager, project } = useSwivuBryntumContext();
  const { entityType, entityId, otherParams, closeEntityManager } = entityManager;
  const [activeTab, setActiveTab] = useState<string>("0");
  const [record, setRecord] = useState<EntityRecord | null>(null);
  const [entityConfig, setEntityConfig] = useState<(typeof ENTITY_CONFIGS)[keyof typeof ENTITY_CONFIGS] | null>(null);

  useEffect(() => {
    if (!entityType || !Object.values(ENTITY_TYPES).includes(entityType as EntityTypeKey) || !entityId) {
      closeEntityManager();
      return;
    }

    const config = ENTITY_CONFIGS[entityType]
    if (!config) {
      closeEntityManager();
      return;
    }

    const record = project.getCrudStore(config.storeKey as string).getById(Number(entityId));
    if (!record) {
      closeEntityManager();
      return;
    }
    setEntityConfig(config);
    setRecord(record as unknown as EntityRecord);
  }, [entityType, entityId, project]);

  useSwivuStore<EntityRecord>({ storeId: entityConfig?.storeKey as string, subscriberFn: ({ record: changedRecord }) => changedRecord.id === record?.id });

  useEffect(() => {
    if (record?.id && entityType) {
      setActiveTab("0");
    }
  }, [record, entityType])

  if (!record || !entityType || !entityConfig) {
    console.warn("EntityManagerProvider: record or entityType or entityConfig is not set", { record, entityType, entityConfig });
    return null;
  }

  return (
    <EntityManagerContext.Provider value={{ record, entityType: entityType as EntityTypeKey, entityConfig, activeTab, setActiveTab, otherParams, closeEntityManager }}>
      {children}
    </EntityManagerContext.Provider>
  );
};

export const useEntityManagerContext = () => {
  const context = useContext(EntityManagerContext);
  if (context === undefined) {
    throw new Error('useEntityManagerContext must be used within an EntityManagerProvider');
  }
  return context;
};